/**
 * <s-scroller> — horizontal scroll row with a progress bar and previous/next buttons.
 *
 * Markup contract (all inside the element):
 * - [data-scroller-track]  the overflow-x scroll container (its children are the items)
 * - [data-scroller-thumb]  optional progress thumb, positioned inside a relative bar
 * - [data-scroller-prev] / [data-scroller-next]  optional buttons (any number, e.g. a mobile overlay pair); disabled at either end
 * Sets `data-overflow` on itself while the track can scroll, so controls can hide otherwise.
 * Arrow buttons scroll by the number of fully visible items; RTL-aware; honors reduced motion.
 */
class SScroller extends HTMLElement {
  #ac;
  #ro;
  #frame = 0;

  connectedCallback() {
    const track = this.querySelector('[data-scroller-track]');
    if (!track) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const thumb = this.querySelector('[data-scroller-thumb]');
    const prevs = [...this.querySelectorAll('[data-scroller-prev]')];
    const nexts = [...this.querySelectorAll('[data-scroller-next]')];

    const update = () => {
      this.#frame = 0;
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);
      const overflow = max > 1;
      this.toggleAttribute('data-overflow', overflow);
      if (thumb) {
        const ratio = Math.min(1, track.clientWidth / Math.max(1, track.scrollWidth));
        const progress = max > 0 ? pos / max : 0;
        thumb.style.width = `${ratio * 100}%`;
        thumb.style.insetInlineStart = `${progress * (1 - ratio) * 100}%`;
      }
      prevs.forEach((b) => (b.disabled = !overflow || pos <= 1));
      nexts.forEach((b) => (b.disabled = !overflow || pos >= max - 1));
    };
    const schedule = () => {
      if (!this.#frame) this.#frame = requestAnimationFrame(update);
    };

    const step = (direction) => {
      const item = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const itemWidth = item ? item.getBoundingClientRect().width + gap : track.clientWidth;
      const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / itemWidth));
      const rtl = getComputedStyle(track).direction === 'rtl';
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: direction * (rtl ? -1 : 1) * itemWidth * perPage, behavior: reduce ? 'auto' : 'smooth' });
    };

    track.addEventListener('scroll', schedule, { passive: true, signal });
    prevs.forEach((b) => b.addEventListener('click', () => step(-1), { signal }));
    nexts.forEach((b) => b.addEventListener('click', () => step(1), { signal }));
    this.#ro = new ResizeObserver(schedule);
    this.#ro.observe(track);
    update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ro?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('s-scroller')) customElements.define('s-scroller', SScroller);
